/**
 * The representations list, decided: an agent's mandates gathered under the
 * client who granted each, with the counts, the artist chips and the identity
 * card that sit over them.
 *
 * Pure. Mandates, the outbox, `/me` and the reader's choices go in; a
 * discriminated view comes out. The copy is `agent.constants.ts`, the reads
 * and the collapse and filter state are `useRepresentationsController.ts`, and
 * the portal that mounts the screen is `agent.constants.ts` too.
 *
 * The Pending tile is dropped, not shown as zero, while the outbox is unread —
 * "0 pending" and "we do not know" are different sentences. And a group is
 * keyed on the client's ONDO id rather than their name, since two clients may
 * share a display name and the id is what a mandate actually records.
 */

import type { AgentMandate, MeResponse, RepresentationRequestMade } from "@ondo/shared";
import { mandateDaysRemaining } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatLongDay } from "../lib/dates.ts";
import { initialsOf } from "../lib/names.ts";
import type { Tile } from "../lib/Tiles.tsx";
import { AGENT_COPY } from "./agent.constants.ts";

/** One row, with every label already decided. */
export interface MandateRow {
  mandateId: string;
  workId: string;
  /** '/agent/ONDO-5592/work/<id>'. Decided here so the view holds no path arithmetic. */
  href: string;
  title: string;
  /** "Peter Doig · 2003 · Oil on canvas", with each missing part dropped. */
  attribution: string;
  /** "200 × 275 cm", or null. The fact a dealer reads without opening anything. */
  dimensions: string | null;
  /** The signed URL for the work's first photograph, or null for none. */
  imageUrl: string | null;
  /** "Assigned 14 March 2026 · For the Basel consignment". */
  assigned: string;
  /** Always AGENT_COPY.active — see the comment there for why it is on the row. */
  status: string;
}

/**
 * The works one client has put in this agent's hands.
 *
 * The grouping is the screen's structure rather than a nicety: an advisory
 * holding eleven works for four collectors reads them as four relationships,
 * and a flat list makes them re-derive that every time they look. It is possible
 * at all because 20260901090000 returns the client's name beside their id.
 */
export interface ClientGroup {
  /** Their ONDO id, which is also the collapse key. */
  key: string;
  name: string;
  initials: string;
  /** "ONDO-1001 · 3 works". No capacity word — see 20260901090000's decision 1. */
  meta: string;
  open: boolean;
  rows: MandateRow[];
}

/** Who is reading, and on what terms. */
export interface IdentityCard {
  initials: string;
  lead: string;
  name: string;
  /** "Art Advisor, Marlow & Finch Art Advisory · d.ellsworth@marlowfinch.art". */
  meta: string;
}

/** One artist chip. `key` is the attributed name, or "" for all of them. */
export interface ArtistFilter {
  key: string;
  label: string;
  count: number;
}

/**
 * What the agent workspace shows.
 *
 * A discriminated union rather than a bag of flags, so "loading and also empty"
 * cannot be represented — the same shape `WorkspaceView` and `WorkRecordView`
 * use.
 */
export type RepresentationsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  // Neither carries a title any more: the portal header above the screen is the
  // one heading, and the list rendering a second one under it was the first
  // thing the rail made redundant. `error` keeps its own, because it replaces
  // the content rather than sitting under a heading that still applies.
  | { kind: "empty"; identity: IdentityCard | null; lead: string; detail: string }
  | {
      kind: "listed";
      identity: IdentityCard | null;
      tiles: Tile[];
      /** Empty when there is nothing to choose between. The view renders no row then. */
      filters: ArtistFilter[];
      activeFilter: string;
      count: string;
      groups: ClientGroup[];
      /** Set when the filter matched nothing, so the list is honestly empty. */
      noMatches: string | null;
    };

export function toRepresentationsView(input: {
  mandates: AgentMandate[] | null;
  error: string | null;
  /** Work id to signed URL. Empty until the second pass resolves; see the controller. */
  images?: ReadonlyMap<string, string>;
  /**
   * The agent's own open requests, for the PENDING tile.
   *
   * Null while it is loading AND when the read failed. A side count must not be
   * able to take down the list it sits over, so a failure here simply drops the
   * tile — see the controller, which swallows that error deliberately.
   */
  requests?: RepresentationRequestMade[] | null;
  me?: MeResponse | null;
  /** Which organisation's workspace this is, so the card names the right firm. */
  orgOndoId?: string | null;
  /** Where a row links to: '/agent/ONDO-5592/work'. */
  workBase: string;
  /** The chosen artist, or "" for all. */
  artist?: string;
  /** ONDO ids of the clients the reader has collapsed. */
  collapsed?: ReadonlySet<string>;
}): RepresentationsView {
  if (input.error !== null) {
    return { kind: "error", title: AGENT_COPY.errorTitle, message: input.error };
  }

  if (input.mandates === null) {
    return { kind: "loading", message: AGENT_COPY.loading };
  }

  const identity = toIdentityCard(input.me ?? null, input.orgOndoId ?? null);

  if (input.mandates.length === 0) {
    return {
      kind: "empty",
      identity,
      lead: AGENT_COPY.empty,
      detail: AGENT_COPY.emptyDetail,
    };
  }

  const artist = input.artist ?? "";
  const shown = artist === "" ? input.mandates : input.mandates.filter((m) => m.attributedName === artist);

  return {
    kind: "listed",
    identity,
    tiles: toTiles(input.mandates, input.requests ?? null),
    filters: toArtistFilters(input.mandates),
    activeFilter: artist,
    count: AGENT_COPY.count(shown.length),
    groups: toClientGroups(shown, {
      images: input.images,
      workBase: input.workBase,
      collapsed: input.collapsed ?? new Set(),
    }),
    noMatches: shown.length === 0 ? AGENT_COPY.noMatches : null,
  };
}

/**
 * The four counts.
 *
 * Every one of them is arithmetic over rows already on screen, except PENDING,
 * which is the agent's own outbox — and that tile is dropped rather than shown
 * as zero when the outbox has not been read, because "0 pending" and "we do not
 * know" are different sentences and only one of them is true.
 */
export function toTiles(
  mandates: AgentMandate[],
  requests: RepresentationRequestMade[] | null,
): Tile[] {
  const clients = new Set(mandates.map((mandate) => mandate.assignedBy));
  const artists = new Set(mandates.map((mandate) => mandate.attributedName));

  const tiles: Tile[] = [
    {
      key: "works",
      label: AGENT_COPY.tiles.works,
      value: String(mandates.length),
      note: AGENT_COPY.tiles.worksNote,
    },
    {
      key: "clients",
      label: AGENT_COPY.tiles.clients,
      value: String(clients.size),
      note: AGENT_COPY.tiles.clientsNote,
    },
    {
      key: "artists",
      label: AGENT_COPY.tiles.artists,
      value: String(artists.size),
      note: AGENT_COPY.tiles.artistsNote,
    },
  ];

  // ENDING SOON, and dropped rather than shown as zero when nothing is — the
  // same rule the Pending tile keeps below, and for a plainer reason here: a
  // dashboard that permanently reads "0 ending soon" is four tiles of furniture
  // for an advisory whose appointments are all open-ended, which is most of
  // them.
  const soon = endingSoon(mandates);
  if (soon > 0) {
    tiles.push({
      key: "endingSoon",
      label: AGENT_COPY.tiles.endingSoon,
      value: String(soon),
      note: AGENT_COPY.tiles.endingSoonNote,
    });
  }

  if (requests !== null) {
    tiles.push({
      key: "pending",
      label: AGENT_COPY.tiles.pending,
      // Open means the owner has not answered and the agent has not taken it
      // back. `outcome` is null for exactly that state.
      value: String(requests.filter((request) => request.outcome === null).length),
      note: AGENT_COPY.tiles.pendingNote,
    });
  }

  return tiles;
}

/**
 * One chip per artist, commonest first, and nothing at all below two.
 *
 * `CollectionToolbar`'s rule: a control with one option is chrome pretending to
 * be a control. Ties break alphabetically so the row does not reshuffle itself
 * between two loads of the same data.
 */
export function toArtistFilters(mandates: AgentMandate[]): ArtistFilter[] {
  const counts = new Map<string, number>();
  for (const mandate of mandates) {
    counts.set(mandate.attributedName, (counts.get(mandate.attributedName) ?? 0) + 1);
  }

  if (counts.size < 2) return [];

  const each = [...counts.entries()]
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));

  return [{ key: "", label: AGENT_COPY.allArtists, count: mandates.length }, ...each];
}

/**
 * The mandates, gathered under whoever appointed them.
 *
 * Keyed on the ONDO id and not the name, because the id is the identifier and
 * two clients may share a display name. Groups come out in the order their first
 * mandate does, which is `created_at desc` from SQL — so the client who most
 * recently put work in your hands is at the top.
 */
export function toClientGroups(
  mandates: AgentMandate[],
  options: {
    images: ReadonlyMap<string, string> | undefined;
    workBase: string;
    collapsed: ReadonlySet<string>;
  },
): ClientGroup[] {
  const groups = new Map<string, ClientGroup>();

  for (const mandate of mandates) {
    const existing = groups.get(mandate.assignedBy);
    const row = toRow(mandate, options.workBase, options.images);

    if (existing) {
      existing.rows.push(row);
      continue;
    }

    // Falls back to the id, which is what this screen showed before the name
    // existed. An API deployed ahead of 20260901090000 sends no name at all, and
    // a panel headed "undefined" would be a worse answer than the identifier the
    // agent already had.
    const client = mandate.assignedByName || mandate.assignedBy;

    groups.set(mandate.assignedBy, {
      key: mandate.assignedBy,
      name: client,
      initials: initialsOf(client),
      meta: "",
      open: !options.collapsed.has(mandate.assignedBy),
      rows: [row],
    });
  }

  // The count is only knowable once the group is complete, which is why `meta`
  // is filled here rather than at construction.
  return [...groups.values()].map((group) => ({
    ...group,
    meta: `${group.key} · ${AGENT_COPY.worksUnder(group.rows.length)}`,
  }));
}

function toRow(
  mandate: AgentMandate,
  workBase: string,
  images?: ReadonlyMap<string, string>,
): MandateRow {
  return {
    mandateId: mandate.mandateId,
    workId: mandate.workId,
    href: `${workBase}/${mandate.workId}`,
    title: mandate.title,
    // The year and the medium are genuinely optional on `ondo.works` — 'c. 1927'
    // and 'undated' are real catalogue values and so is nothing at all — so each
    // separator goes with the part it separates rather than leaving a trailing
    // " · ".
    attribution: [mandate.attributedName, mandate.year, mandate.medium]
      // Truthy rather than `!== null`, which is what this was: `medium` and
      // `dimensions` arrive from a migration this build may be running ahead
      // of — the API deploys on push and migrations are applied by hand — and
      // `undefined` would have passed a null check and joined the word
      // "undefined" into the catalogue line.
      .filter((part) => Boolean(part))
      .join(" · "),
    dimensions: mandate.dimensions,
    // Null covers both "the work has no photograph" and "its path did not sign",
    // which render the same and should: an empty frame is the honest answer to
    // each, and a broken image would claim the second was a fault.
    imageUrl: images?.get(mandate.workId) ?? null,
    assigned: describeAssignment(mandate),
    status: AGENT_COPY.active,
  };
}

/**
 * "Assigned 14 March 2026 · For the Basel consignment".
 *
 * The date is where the design puts "121 days remaining", and it is the true
 * thing that fits there: when the mandate began is recorded, when it ends is
 * not. It says "Assigned" rather than repeating the client's id, which the group
 * head two lines above already carries.
 */
export function describeAssignment(
  mandate: Pick<AgentMandate, "createdAt" | "note" | "term">,
  now = new Date(),
): string {
  const on = formatLongDay(mandate.createdAt);

  const parts = [`${AGENT_COPY.assignedOn} ${on}`, describeTerm(mandate.term, now)];
  if (mandate.note) parts.push(mandate.note);

  return parts.filter((part) => part !== "").join(" · ");
}

/**
 * "121 days remaining", or nothing at all.
 *
 * THE LINE 20260901090000 REFUSED, and it is worth being precise about what
 * changed rather than just building it. That migration's objection was not to
 * the sentence but to its truth: "inventing '121 days' from created_at would
 * put a deadline on screen that nothing enforces." This counts down to
 * `expiresAt`, which `ondo.live_mandate_ids()` reads in the statement that
 * answers every permission question — so the number on screen is the number the
 * database acts on.
 *
 * EMPTY FOR AN OPEN-ENDED MANDATE, which is most of them. See
 * `AGENT_COPY.term.openEnded`: labelling the default would put a phrase on
 * nearly every row to say nothing is going to happen.
 *
 * AND EMPTY FOR A LAPSED ONE, which cannot be reached from this screen and is
 * handled anyway. `ondo.my_agent_mandates()` reads the liveness set, so a term
 * in the past is not a row an agent can see — but a clock a few seconds off, or
 * a list held open across the moment a term runs out, would otherwise print
 * "−1 days remaining". `mandateDaysRemaining` returns a negative number rather
 * than clamping precisely so this can tell the two apart.
 *
 * `now` IS A PARAMETER, per `covenantState`: a test should not have to wait.
 */
function describeTerm(term: AgentMandate["term"], now: Date): string {
  const days = mandateDaysRemaining({ expiresAt: term.expiresAt }, now);

  if (days === null) return AGENT_COPY.term.openEnded;
  if (days < 0) return AGENT_COPY.term.openEnded;
  if (days === 0) return AGENT_COPY.term.endsToday;

  return AGENT_COPY.term.remaining(days);
}

/**
 * How many of these mandates run out within a month.
 *
 * THIRTY DAYS AND NOT THE MONTH WARNING'S CALENDAR MONTH, and the difference is
 * deliberate rather than an oversight. The warning is a message about one
 * appointment and has to land on the date the parties would name; this is a
 * COUNT on a dashboard tile, and "within 30 days" is a span a reader holds in
 * their head where "within one calendar month" is not.
 *
 * Counts only live terms. A lapsed mandate is not in this list at all, so there
 * is nothing here to exclude — see the tile's own comment for why an Expired
 * count would always be zero.
 */
export function endingSoon(mandates: AgentMandate[], now = new Date()): number {
  return mandates.filter((mandate) => {
    const days = mandateDaysRemaining({ expiresAt: mandate.term.expiresAt }, now);
    return days !== null && days >= 0 && days <= 30;
  }).length;
}

/**
 * The card over the list: who is signed in, and on whose behalf.
 *
 * Duplicating the rail's identity card is the design's own decision and a
 * defensible one — the rail says who you are in every workspace, and this says
 * which seat you are reading THIS list from. A member of an advisory holds
 * mandates through the firm, so "Art Advisor, Marlow & Finch" is the fact that
 * explains why somebody else's pictures are on the screen.
 *
 * Null when `/me` is not loaded. The list is the screen; a card about the reader
 * must never hold it up.
 */
export function toIdentityCard(
  me: MeResponse | null,
  orgOndoId: string | null,
): IdentityCard | null {
  if (me === null) return null;

  const name = me.principal.displayName;
  const membership =
    orgOndoId === null
      ? null
      : (me.memberships.find((held) => held.organisation.ondoId === orgOndoId) ?? null);

  const seat = membership
    ? [membership.title, membership.organisation.displayName].filter(Boolean).join(", ")
    : null;

  return {
    initials: initialsOf(name),
    lead: AGENT_COPY.signedInAs,
    name,
    meta: [seat, me.user.email].filter((part) => part !== null && part !== "").join(" · "),
  };
}

/** The same translation the work record makes. See `describeError` there. */
export function describeMandateError(cause: unknown): string {
  if (cause instanceof ApiError && cause.message) return cause.message;
  return AGENT_COPY.errorTitle;
}
